import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest, kvRequest, mgmtRequest } from "./authConfig";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const COLORS = {
  bg:         "#0f1117",
  surface:    "#1a1d27",
  card:       "#1e2235",
  border:     "#2a2f45",
  accent:     "#5b7fff",
  accentDim:  "#1e2a55",
  success:    "#22c55e",
  successDim: "#052e16",
  danger:     "#ef4444",
  dangerDim:  "#2d0e0e",
  warn:       "#f59e0b",
  text:       "#e8eaf0",
  muted:      "#8892aa",
  dim:        "#4b5370",
  orange:     "#e85d1a",
};

// ─── SVG Components ───────────────────────────────────────────────────────────
function H56Logo({ size = 36 }) {
  const w = size * 1.75;
  return (
    <svg width={w} height={size} viewBox="0 0 70 40" fill="none">
      <defs><clipPath id="h56clip"><rect width="70" height="40" rx="6" /></clipPath></defs>
      <g clipPath="url(#h56clip)">
        <rect x="0"  y="0" width="35" height="40" fill="#2c4163" />
        <rect x="35" y="0" width="35" height="40" fill="#e85d1a" />
        <text x="17.5" y="30" textAnchor="middle" fill="white" fontSize="28" fontWeight="800"
          fontFamily="'Arial','Helvetica',sans-serif">H</text>
        <text x="52.5" y="30" textAnchor="middle" fill="white" fontSize="26" fontWeight="700"
          fontFamily="'Arial','Helvetica',sans-serif">56</text>
      </g>
    </svg>
  );
}

function KeyVaultIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#1a3a6e" stroke="#2e5baa" strokeWidth="1.2" />
      <circle cx="10" cy="10.5" r="3.2" stroke="#f5a623" strokeWidth="1.8" fill="none" />
      <line x1="12.8" y1="12.8" x2="17"   y2="17"   stroke="#f5a623" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="15.5" y1="15.5" x2="15.5" y2="17.2" stroke="#f5a623" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="17"   y1="14.2" x2="17"   y2="16"   stroke="#f5a623" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function MicrosoftIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21">
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

// ─── Reusable UI ──────────────────────────────────────────────────────────────
function Badge({ children, color = "accent" }) {
  const map = {
    accent:  { bg: COLORS.accentDim,  text: COLORS.accent  },
    success: { bg: COLORS.successDim, text: COLORS.success },
    danger:  { bg: COLORS.dangerDim,  text: COLORS.danger  },
    warn:    { bg: "#2d1f00",         text: COLORS.warn    },
    ms:      { bg: "#1a2a1a",         text: "#7fba00"      },
  };
  const c = map[color] || map.accent;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
      padding: "2px 8px", borderRadius: 20,
      background: c.bg, color: c.text, textTransform: "uppercase",
    }}>{children}</span>
  );
}

function Input({ label, value, onChange, placeholder, mono, type = "text", onKeyDown }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && <label style={{ fontSize: 12, color: COLORS.muted, fontWeight: 500, letterSpacing: "0.03em" }}>{label}</label>}
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown} placeholder={placeholder}
        style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 8, color: COLORS.text, fontSize: 13,
          fontFamily: mono ? "monospace" : "inherit",
          padding: "9px 12px", outline: "none",
          transition: "border-color 0.15s", width: "100%", boxSizing: "border-box",
        }}
        onFocus={e => e.target.style.borderColor = COLORS.accent}
        onBlur={e  => e.target.style.borderColor = COLORS.border}
      />
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, small, fullWidth, icon }) {
  const variants = {
    primary: { background: "#5b7fff", color: "#fff",    border: "none" },
    orange:  { background: "#e85d1a", color: "#fff",    border: "none" },
    ms:      { background: "#fff",    color: "#1a1a1a", border: "1px solid #d0d0d0" },
    ghost:   { background: "transparent", color: COLORS.muted, border: `1px solid ${COLORS.border}` },
    danger:  { background: COLORS.dangerDim,  color: COLORS.danger,  border: "1px solid #5a1a1a" },
    success: { background: COLORS.successDim, color: COLORS.success, border: "1px solid #0a4a20" },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, borderRadius: 8,
      padding: small ? "6px 14px" : "10px 22px",
      fontSize: small ? 12 : 14, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.45 : 1,
      transition: "opacity 0.15s, transform 0.1s",
      whiteSpace: "nowrap", letterSpacing: "0.02em", lineHeight: 1,
      width: fullWidth ? "100%" : "auto",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={e   => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      {icon && icon}{children}
    </button>
  );
}

function Card({ children, title, icon, action }) {
  return (
    <div style={{
      background: COLORS.card, border: `1px solid ${COLORS.border}`,
      borderRadius: 14, padding: "20px 22px",
      display: "flex", flexDirection: "column", gap: 16,
    }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {icon && (typeof icon === "string" ? <span style={{ fontSize: 16 }}>{icon}</span> : icon)}
            <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.text }}>{title}</span>
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function SecretRow({ name, value, onDelete }) {
  const [revealed, setRevealed] = useState(false);
  const [copied,   setCopied]   = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", background: COLORS.surface,
      borderRadius: 9, border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 2 }}>{name}</div>
        <div style={{ fontSize: 12, color: COLORS.muted, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {revealed ? value : "••••••••••••••••"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <Btn small variant="ghost" onClick={() => setRevealed(r => !r)}>{revealed ? "Hide" : "Show"}</Btn>
        <Btn small variant="ghost" onClick={copy}>{copied ? "✓" : "Copy"}</Btn>
        <Btn small variant="danger" onClick={() => onDelete(name)}>Del</Btn>
      </div>
    </div>
  );
}

function Spinner({ size = 20, color = COLORS.accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: "kv-spin 0.8s linear infinite" }}>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round" />
      <style>{`@keyframes kv-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  const c = type === "error" ? COLORS.danger : COLORS.success;
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 999,
      background: COLORS.card, border: `1px solid ${c}`,
      borderRadius: 10, padding: "12px 18px",
      color: c, fontSize: 13, fontWeight: 500,
      maxWidth: 360, boxShadow: "0 4px 24px #0008",
    }}>{msg}</div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onPasswordLogin }) {
  const { instance, inProgress } = useMsal();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);
  const msalBusy = inProgress !== InteractionStatus.None;

  const handlePasswordLogin = async () => {
    if (!username.trim() || !password.trim()) { setError("Please enter your username and password."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { username: username.trim(), password });
      sessionStorage.setItem("kv_user", JSON.stringify({ ...res.data, authType: "password" }));
      onPasswordLogin({ ...res.data, authType: "password" });
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid username or password.");
    } finally { setLoading(false); }
  };

  const onEnter = e => { if (e.key === "Enter") handlePasswordLogin(); };

  return (
    <div style={{
      minHeight: "100vh", background: COLORS.bg,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter','Segoe UI',sans-serif",
    }}>
      <div style={{
        position: "fixed", pointerEvents: "none", width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(91,127,255,0.06) 0%, transparent 70%)",
        top: "50%", left: "50%", transform: "translate(-50%,-50%)",
      }} />
      <div style={{
        background: COLORS.card, border: `1px solid ${COLORS.border}`,
        borderRadius: 20, padding: "44px 40px", width: "100%", maxWidth: 400,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 22,
        boxShadow: "0 8px 48px #00000055",
      }}>
        <H56Logo size={42} />
        <div style={{ textAlign: "center", width: "100%" }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Vault Brew-vi</div>
          <div style={{ fontSize: 12, color: COLORS.muted }}>Sign in to manage Azure Key Vault secrets</div>
        </div>

        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 11, color: COLORS.muted, textAlign: "center", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Azure AD — Key Vault access based on your role
          </div>
          <Btn variant="ms" onClick={() => instance.loginRedirect(loginRequest).catch(console.error)}
            disabled={msalBusy} fullWidth icon={<MicrosoftIcon size={18} />}>
            {msalBusy ? "Redirecting…" : "Sign in with Microsoft"}
          </Btn>
        </div>

        <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, height: 1, background: COLORS.border }} />
          <span style={{ fontSize: 11, color: COLORS.dim, fontWeight: 600 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: COLORS.border }} />
        </div>

        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 11, color: COLORS.muted, textAlign: "center", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            DevOps Team — shared password
          </div>
          <Input label="Username" value={username}
            onChange={v => { setUsername(v); setError(""); }}
            placeholder="Enter your username" onKeyDown={onEnter} />
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={{ fontSize: 12, color: COLORS.muted, fontWeight: 500 }}>Password</label>
            <div style={{ position: "relative" }}>
              <input type={showPw ? "text" : "password"} value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                onKeyDown={onEnter} placeholder="Enter your password"
                style={{
                  background: COLORS.surface,
                  border: `1px solid ${error ? COLORS.danger : COLORS.border}`,
                  borderRadius: 8, color: COLORS.text, fontSize: 13,
                  padding: "9px 44px 9px 12px", outline: "none",
                  width: "100%", boxSizing: "border-box", transition: "border-color 0.15s",
                }}
                onFocus={e => e.target.style.borderColor = error ? COLORS.danger : COLORS.accent}
                onBlur={e  => e.target.style.borderColor = error ? COLORS.danger : COLORS.border}
              />
              <button onClick={() => setShowPw(s => !s)} style={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", color: COLORS.muted, cursor: "pointer", fontSize: 12,
              }}>{showPw ? "Hide" : "Show"}</button>
            </div>
          </div>
          {error && (
            <div style={{
              background: COLORS.dangerDim, border: `1px solid ${COLORS.danger}44`,
              borderRadius: 8, padding: "8px 12px",
              fontSize: 12, color: COLORS.danger, display: "flex", alignItems: "center", gap: 6,
            }}>⚠ {error}</div>
          )}
          <Btn variant="orange" onClick={handlePasswordLogin}
            disabled={loading || !username || !password} fullWidth>
            {loading ? "Signing in…" : "Sign In"}
          </Btn>
        </div>
        <div style={{ fontSize: 11, color: COLORS.dim, textAlign: "center" }}>
          Access restricted to H56 team members only
        </div>
      </div>
      <div style={{ marginTop: 20, fontSize: 11, color: COLORS.dim }}>
        Azure Key Vault Manager · H56 © {new Date().getFullYear()}
      </div>
    </div>
  );
}

// ─── User Menu ────────────────────────────────────────────────────────────────
function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const { instance } = useMsal();
  const isSSO    = user.authType === "sso";
  const name     = user.name || user.username;
  const email    = user.email || user.username;
  const initials = name.split(/[\s._@-]/).filter(Boolean).map(p => p[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = () => {
    setOpen(false);
    sessionStorage.removeItem("kv_user");
    if (isSSO) instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    else onLogout();
  };

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "none", border: `1px solid ${COLORS.border}`,
        borderRadius: 8, padding: "5px 10px", cursor: "pointer", color: COLORS.text,
      }}>
        <div style={{
          width: 26, height: 26, borderRadius: "50%",
          background: isSSO ? "#0078d4" : COLORS.orange,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 700, color: "#fff",
        }}>{initials}</div>
        <span style={{ fontSize: 13, fontWeight: 500, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        <span style={{ fontSize: 10, color: COLORS.muted }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            background: COLORS.card, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: 8, minWidth: 220, zIndex: 100, boxShadow: "0 4px 20px #0006",
          }}>
            <div style={{ padding: "8px 12px", borderBottom: `1px solid ${COLORS.border}`, marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{name}</div>
              <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{email}</div>
              <div style={{ marginTop: 6 }}>
                {isSSO ? <Badge color="ms">Azure AD · SSO</Badge> : <Badge>DevOps · Password</Badge>}
              </div>
            </div>
            {isSSO && (
              <div style={{ padding: "6px 12px", fontSize: 11, color: COLORS.dim, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 4 }}>
                Key Vault access controlled by your Azure role
              </div>
            )}
            <button onClick={handleLogout} style={{
              width: "100%", textAlign: "left", background: "none", border: "none",
              padding: "8px 12px", borderRadius: 6,
              color: COLORS.danger, fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>↩ Sign out</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function MainApp({ user, onLogout }) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isSSO   = user.authType === "sso";

  const [vault,       setVault]       = useState("");
  const [vaultList,   setVaultList]   = useState([]);
  const [vaultsLoading, setVaultsLoading] = useState(false);
  const [tab,         setTab]         = useState("list");
  const [secrets,     setSecrets]     = useState([]);
  const [keyword,     setKeyword]     = useState("");
  const [prefix,      setPrefix]      = useState("");
  const [loading,     setLoading]     = useState(false);
  const [hasFetched,  setHasFetched]  = useState(false);
  const [toast,       setToast]       = useState(null);
  const [newName,     setNewName]     = useState("");
  const [newValue,    setNewValue]    = useState("");
  const [updateName,  setUpdateName]  = useState("");
  const [updateValue, setUpdateValue] = useState("");

  // Seed state
  const [srcEnv,         setSrcEnv]         = useState("");
  const [tgtEnv,         setTgtEnv]         = useState("");
  const [seedPrefix,     setSeedPrefix]     = useState("RF");
  const [seedPreview,    setSeedPreview]    = useState([]);
  const [seedResults,    setSeedResults]    = useState(null);
  const [seedLoading,    setSeedLoading]    = useState(false);
  const [seedStep,       setSeedStep]       = useState("idle");
  const [seedOverwrite,  setSeedOverwrite]  = useState(false);
  const [targetVault,    setTargetVault]    = useState("");

  // Delete by prefix state
  const [delPrefix,      setDelPrefix]      = useState("RF");
  const [delEnvCode,     setDelEnvCode]     = useState("");
  const [delPreview,     setDelPreview]     = useState([]);
  const [delResults,     setDelResults]     = useState(null);
  const [delLoading,     setDelLoading]     = useState(false);
  const [delStep,        setDelStep]        = useState("idle");

  // Search tab state
  const [searchQuery,    setSearchQuery]    = useState("");
  const [searchResults,  setSearchResults]  = useState([]);
  const [searchLoading,  setSearchLoading]  = useState(false);
  const [searchDone,     setSearchDone]     = useState(false);


  const notify = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getHeaders = useCallback(async () => {
    if (isSSO) {
      try {
        const result = await instance.acquireTokenSilent(kvRequest(account));
        return { Authorization: `Bearer ${result.accessToken}`, "X-Auth-Type": "sso" };
      } catch {
        try {
          const result = await instance.acquireTokenPopup(kvRequest(account));
          return { Authorization: `Bearer ${result.accessToken}`, "X-Auth-Type": "sso" };
        } catch {
          instance.loginRedirect(loginRequest);
          throw new Error("Token acquisition failed");
        }
      }
    }
    return { "X-Username": user.username, "X-Auth-Type": "password" };
  }, [isSSO, instance, account, user]);

  // Fetch available Key Vaults on mount using user's management token
  const fetchVaults = useCallback(async () => {
    setVaultsLoading(true);
    try {
      let headers;
      if (isSSO) {
        let mgmtResult, kvResult;
        try {
          mgmtResult = await instance.acquireTokenSilent(mgmtRequest(account));
        } catch {
          mgmtResult = await instance.acquireTokenPopup(mgmtRequest(account));
        }
        try {
          kvResult = await instance.acquireTokenSilent(kvRequest(account));
        } catch {
          kvResult = await instance.acquireTokenPopup(kvRequest(account));
        }
        headers = {
          Authorization: `Bearer ${mgmtResult.accessToken}`,
          "X-Kv-Token": kvResult.accessToken,
          "X-Auth-Type": "sso",
        };
      } else {
        headers = { "X-Username": user.username, "X-Auth-Type": "password" };
      }
      const res = await axios.get(`${API}/vaults`, { headers });
      setVaultList(res.data.vaults || []);
    } catch (e) {
      console.error("Failed to fetch vaults:", e);
    } finally { setVaultsLoading(false); }
  }, [isSSO, instance, account, user]);

  useEffect(() => { fetchVaults(); }, [fetchVaults]);

  const fetchSecrets = useCallback(async () => {
    if (!vault.trim()) { notify("Enter a Key Vault name first", "error"); return; }
    setLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.get(`${API}/secrets`, {
        params: { vault_name: vault.trim(), keyword, prefix, show_values: true, limit: 200 },
        headers,
      });
      setSecrets(res.data.secrets || []);
      setHasFetched(true);
    } catch (e) {
      notify(e.response?.data?.detail || "Failed to fetch secrets", "error");
    } finally { setLoading(false); }
  }, [vault, keyword, prefix, getHeaders]);

  const searchSecrets = async () => {
    if (!vault.trim()) { notify("Select a Key Vault first", "error"); return; }
    if (!searchQuery.trim()) { notify("Enter a search query", "error"); return; }
    setSearchLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.get(`${API}/secrets`, {
        params: { vault_name: vault.trim(), keyword: searchQuery.trim(), show_values: true, limit: 200 },
        headers,
      });
      setSearchResults(res.data.secrets || []);
      setSearchDone(true);
    } catch (e) {
      notify(e.response?.data?.detail || "Search failed", "error");
    } finally { setSearchLoading(false); }
  };

  const createSecret = async () => {
    if (!vault.trim() || !newName.trim() || !newValue.trim()) { notify("All fields required", "error"); return; }
    setLoading(true);
    try {
      const headers = await getHeaders();
      await axios.post(`${API}/secrets`, { name: newName.trim(), value: newValue.trim() },
        { params: { vault_name: vault.trim() }, headers });
      notify(`Secret '${newName}' created successfully`);
      setNewName(""); setNewValue("");
    } catch (e) { notify(e.response?.data?.detail || "Failed to create secret", "error"); }
    finally { setLoading(false); }
  };

  const updateSecret = async () => {
    if (!vault.trim() || !updateName.trim() || !updateValue.trim()) { notify("All fields required", "error"); return; }
    setLoading(true);
    try {
      const headers = await getHeaders();
      await axios.post(`${API}/secrets/${updateName.trim()}/update`,
        { name: updateName.trim(), value: updateValue.trim() },
        { params: { vault_name: vault.trim() }, headers });
      notify(`Secret '${updateName}' updated successfully`);
      setUpdateValue("");
    } catch (e) { notify(e.response?.data?.detail || "Failed to update secret", "error"); }
    finally { setLoading(false); }
  };

  const deleteSecret = async (name) => {
    if (!window.confirm(`Delete secret '${name}'?`)) return;
    setLoading(true);
    try {
      const headers = await getHeaders();
      await axios.delete(`${API}/secrets/${name}`, { params: { vault_name: vault.trim() }, headers });
      notify(`Secret '${name}' deleted`);
      setSecrets(s => s.filter(x => x.name !== name));
    } catch (e) { notify(e.response?.data?.detail || "Failed to delete secret", "error"); }
    finally { setLoading(false); }
  };

  // ── Seed Environment (Clone) ──────────────────────────────────────────────
  const previewSeed = async () => {
    if (!vault.trim() || !srcEnv.trim() || !tgtEnv.trim()) {
      notify("All fields required", "error"); return;
    }
    if (srcEnv.trim().toLowerCase() === tgtEnv.trim().toLowerCase()) {
      notify("Source and target env codes must be different", "error"); return;
    }
    setSeedLoading(true); setSeedResults(null);
    try {
      const headers = await getHeaders();
      const res = await axios.post(`${API}/clone/preview`,
        { prefix: seedPrefix.trim() || "RF", source_env: srcEnv.trim(), target_env: tgtEnv.trim() },
        { params: { vault_name: vault.trim() }, headers }
      );
      const secrets = res.data.secrets || [];
      if (secrets.length === 0) { notify(`No secrets found matching '${seedPrefix}-${srcEnv}-*'`, "error"); setSeedLoading(false); return; }
      setSeedPreview(secrets.map(s => ({
        source_name: s.source_name,
        target_name: s.target_name,
        value: s.value,
      })));
      setSeedStep("preview");
    } catch (e) { notify(e.response?.data?.detail || "Failed to fetch preview", "error"); }
    finally { setSeedLoading(false); }
  };

  const updateSeedValue = (index, newValue) => {
    setSeedPreview(prev => prev.map((item, i) => i === index ? { ...item, value: newValue } : item));
  };

  const confirmSeed = async () => {
    setSeedStep("cloning"); setSeedLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.post(`${API}/clone/execute`,
        { secrets: seedPreview, overwrite: seedOverwrite },
        { params: { vault_name: vault.trim(), target_vault_name: targetVault.trim() || "" }, headers }
      );
      setSeedResults(res.data);
      setSeedStep("done");
      const { created, overwritten, skipped, failed } = res.data;
      if (failed === 0) notify(`${created} created, ${overwritten} overwritten, ${skipped} skipped`);
      else notify(`${created} created, ${overwritten} overwritten, ${skipped} skipped, ${failed} failed`, "error");
    } catch (e) {
      notify(e.response?.data?.detail || "Clone failed", "error");
      setSeedStep("preview");
    } finally { setSeedLoading(false); }
  };

  const resetSeed = () => {
    setSrcEnv(""); setTgtEnv(""); setSeedPrefix("RF"); setTargetVault("");
    setSeedPreview([]); setSeedResults(null); setSeedStep("idle"); setSeedOverwrite(false);
  };

  // ── Delete by Prefix ──────────────────────────────────────────────────────
  const previewDelete = async () => {
    if (!vault.trim() || !delEnvCode.trim()) {
      notify("Vault name and Env Code are required", "error"); return;
    }
    setDelLoading(true); setDelResults(null);
    try {
      const headers = await getHeaders();
      const res = await axios.post(`${API}/delete/preview`,
        { prefix: delPrefix.trim() || "RF", env_code: delEnvCode.trim() },
        { params: { vault_name: vault.trim() }, headers }
      );
      const secrets = res.data.secrets || [];
      if (secrets.length === 0) { notify(`No secrets found matching '${delPrefix}-${delEnvCode}-*'`, "error"); setDelLoading(false); return; }
      setDelPreview(secrets.map(s => ({ name: s.name, selected: true })));
      setDelStep("preview");
    } catch (e) { notify(e.response?.data?.detail || "Failed to fetch preview", "error"); }
    finally { setDelLoading(false); }
  };

  const toggleDelSecret = (index) => {
    setDelPreview(prev => prev.map((item, i) => i === index ? { ...item, selected: !item.selected } : item));
  };

  const toggleAllDelSecrets = (selected) => {
    setDelPreview(prev => prev.map(item => ({ ...item, selected })));
  };

  const confirmDelete = async () => {
    const toDelete = delPreview.filter(s => s.selected).map(s => s.name);
    if (toDelete.length === 0) { notify("No secrets selected for deletion", "error"); return; }
    if (!window.confirm(`Are you sure you want to delete ${toDelete.length} secret(s)? This action cannot be undone.`)) return;
    setDelStep("deleting"); setDelLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.post(`${API}/delete/execute`,
        { secret_names: toDelete },
        { params: { vault_name: vault.trim() }, headers }
      );
      setDelResults(res.data);
      setDelStep("done");
      const { deleted, failed } = res.data;
      if (failed === 0) notify(`${deleted} secret(s) deleted successfully`);
      else notify(`${deleted} deleted, ${failed} failed`, "error");
    } catch (e) {
      notify(e.response?.data?.detail || "Delete failed", "error");
      setDelStep("preview");
    } finally { setDelLoading(false); }
  };

  const resetDelete = () => {
    setDelPrefix("RF"); setDelEnvCode("");
    setDelPreview([]); setDelResults(null); setDelStep("idle");
  };


  const tabs = [
    { id: "list",   label: "List Secrets"       },
    { id: "search", label: "Search Secrets"     },
    { id: "create", label: "Create Secret"      },
    { id: "update", label: "Update Secret"      },
    { id: "seed",   label: "Clone Environment"  },
    { id: "delete", label: "Delete by Prefix"   },
  ];

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, color: COLORS.text, fontFamily: "'Inter','Segoe UI',sans-serif", paddingBottom: 48 }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface,
        padding: "0 32px", display: "flex", alignItems: "center", gap: 16, height: 56,
      }}>
        <H56Logo size={32} />
        <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.text, letterSpacing: "-0.01em" }}>Vault Brew-vi</span>
        <Badge>H56</Badge>
        {isSSO && <Badge color="ms">Azure AD</Badge>}
        <div style={{ marginLeft: "auto" }}><UserMenu user={user} onLogout={onLogout} /></div>
      </div>

      <div style={{ maxWidth: 900, margin: "32px auto", padding: "0 24px" }}>
        {/* Vault */}
        <Card title="Key Vault" icon={<KeyVaultIcon size={22} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={{ fontSize: 12, color: COLORS.muted, fontWeight: 500, letterSpacing: "0.03em" }}>Vault Name</label>
            <input
              type="text"
              value={vault}
              onChange={e => setVault(e.target.value)}
              placeholder="e.g. kv-sdi-dev-euw-01"
              style={{
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.text, fontSize: 13,
                fontFamily: "monospace", padding: "9px 12px", outline: "none",
                width: "100%", boxSizing: "border-box",
              }}
            />
          </div>
          {vault && (
            <div style={{ fontSize: 12, color: COLORS.muted, fontFamily: "monospace", marginTop: -6 }}>
              → https://{vault.toLowerCase()}.vault.azure.net/
            </div>
          )}
          {isSSO && (
            <div style={{ fontSize: 11, color: "#7fba00", background: "#1a2a1a", border: "1px solid #7fba0033", borderRadius: 8, padding: "6px 12px", marginTop: -6 }}>
              🔐 Accessing Key Vault using your Azure AD identity — access controlled by your Key Vault role
            </div>
          )}
        </Card>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 2, margin: "24px 0 16px", borderBottom: `1px solid ${COLORS.border}`, overflowX: "auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "8px 16px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
              color: tab === t.id ? COLORS.accent : COLORS.muted,
              borderBottom: tab === t.id ? `2px solid ${COLORS.accent}` : "2px solid transparent",
              marginBottom: -1, transition: "color 0.15s",
            }}>{t.label}</button>
          ))}
        </div>

        {/* LIST */}
        {tab === "list" && (
          <Card title="Secrets" icon={<KeyVaultIcon size={20} />} action={
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {secrets.length > 0 && <Badge>{secrets.length} secrets</Badge>}
              <Btn variant="orange" onClick={fetchSecrets} disabled={loading || !vault.trim()}>
                {loading ? <><Spinner size={14} color="#fff" /> Loading…</> : "Fetch Secrets"}
              </Btn>
            </div>
          }>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Input label="Environment Name" value={keyword} onChange={setKeyword} placeholder="e.g. qa01slb" />
              <Input label="Prefix filter"  value={prefix}  onChange={setPrefix}  placeholder="e.g. RF" mono />
            </div>
            {secrets.length === 0 && !loading && !hasFetched && (
              <div style={{ textAlign: "center", padding: "32px 0", color: COLORS.dim, fontSize: 13 }}>
                Enter a vault name and click Fetch Secrets
              </div>
            )}
            {secrets.length === 0 && !loading && hasFetched && (
              <div style={{ textAlign: "center", padding: "32px 0", color: COLORS.warn, fontSize: 13 }}>
                ⚠ No secrets found{keyword || prefix ? ` matching ${prefix ? `prefix "${prefix}"` : ""}${keyword && prefix ? " and " : ""}${keyword ? `keyword "${keyword}"` : ""}` : ""}. Try adjusting your filters.
              </div>
            )}
            {loading && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "40px 0" }}>
                <Spinner size={32} color={COLORS.accent} />
                <span style={{ fontSize: 13, color: COLORS.muted, fontWeight: 500 }}>Fetching secrets…</span>
              </div>
            )}
            {secrets.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 460, overflowY: "auto" }}>
                {secrets.map(s => <SecretRow key={s.name} name={s.name} value={s.value} onDelete={deleteSecret} />)}
              </div>
            )}
          </Card>
        )}

        {/* SEARCH */}
        {tab === "search" && (
          <Card title="Search Secrets" icon="🔍" action={
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {searchResults.length > 0 && <Badge>{searchResults.length} found</Badge>}
              <Btn variant="orange" onClick={searchSecrets} disabled={searchLoading || !vault.trim() || !searchQuery.trim()}>
                {searchLoading ? <><Spinner size={14} color="#fff" /> Searching…</> : "Search"}
              </Btn>
            </div>
          }>
            <Input label="Search by name" value={searchQuery} onChange={setSearchQuery} placeholder="Enter secret name or keyword to search…" mono />
            {searchResults.length === 0 && !searchLoading && !searchDone && (
              <div style={{ textAlign: "center", padding: "32px 0", color: COLORS.dim, fontSize: 13 }}>
                Enter a keyword and click Search to find secrets
              </div>
            )}
            {searchResults.length === 0 && !searchLoading && searchDone && (
              <div style={{ textAlign: "center", padding: "32px 0", color: COLORS.warn, fontSize: 13 }}>
                ⚠ No secrets found matching "{searchQuery}". Try a different keyword.
              </div>
            )}
            {searchLoading && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: "40px 0" }}>
                <Spinner size={32} color={COLORS.accent} />
                <span style={{ fontSize: 13, color: COLORS.muted, fontWeight: 500 }}>Searching…</span>
              </div>
            )}
            {searchResults.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 460, overflowY: "auto" }}>
                {searchResults.map(s => <SecretRow key={s.name} name={s.name} value={s.value} onDelete={deleteSecret} />)}
              </div>
            )}
          </Card>
        )}

        {/* CREATE */}
        {tab === "create" && (
          <Card title="Create New Secret" icon="➕">
            <Input label="Secret Name"  value={newName}  onChange={setNewName}  placeholder="e.g. MY-DB-PASSWORD" mono />
            <Input label="Secret Value" value={newValue} onChange={setNewValue} placeholder="Enter the secret value" type="password" />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="success" onClick={createSecret} disabled={loading || !vault.trim() || !newName || !newValue}>
                {loading ? "Creating…" : "Create Secret"}
              </Btn>
            </div>
          </Card>
        )}

        {/* UPDATE */}
        {tab === "update" && (
          <Card title="Update Existing Secret" icon="✏️">
            <Input label="Secret Name" value={updateName}  onChange={setUpdateName}  placeholder="Exact secret name to update" mono />
            <Input label="New Value"   value={updateValue} onChange={setUpdateValue} placeholder="New secret value" type="password" />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={updateSecret} disabled={loading || !vault.trim() || !updateName || !updateValue}>
                {loading ? "Updating…" : "Update Secret"}
              </Btn>
            </div>
          </Card>
        )}

        {/* SEED / CLONE */}
        {tab === "seed" && (
          <Card title="Clone Environment" icon="🌱" action={
            seedStep !== "idle" && <Btn small variant="ghost" onClick={resetSeed}>↺ Reset</Btn>
          }>
            <div style={{ background: COLORS.accentDim, border: `1px solid ${COLORS.accent}33`, borderRadius: 10, padding: "12px 16px", fontSize: 12, color: COLORS.muted, lineHeight: 1.7 }}>
              <span style={{ fontWeight: 600, color: COLORS.accent }}>How it works: </span>
              Enter prefix (e.g. <code>RF</code>), source env (e.g. <code>qa01slb</code>) and target env (e.g. <code>qa02slb</code>).
              All <code>RF-qa01slb-*</code> secrets are cloned as <code>RF-qa02slb-*</code>. Source secrets are never modified.
              You can <strong>edit values</strong> before saving and choose to <strong>overwrite</strong> existing target secrets.
              Optionally set a <strong>Target Vault</strong> to clone secrets into a different Key Vault.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Input label="Secret Prefix" value={seedPrefix} onChange={setSeedPrefix} placeholder="e.g. RF" mono />
              <Input label="Source Env Code" value={srcEnv} onChange={setSrcEnv} placeholder="e.g. qa01slb" mono />
              <Input label="Target Env Code" value={tgtEnv} onChange={setTgtEnv} placeholder="e.g. qa02slb" mono />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <Input label="Target Vault (optional — leave empty to use the same vault above)" value={targetVault} onChange={setTargetVault} placeholder="e.g. kv-sdi-test-euw-01" mono />
            </div>
            {targetVault.trim() && (
              <div style={{ fontSize: 12, color: COLORS.muted, fontFamily: "monospace", marginTop: -6 }}>
                → https://{targetVault.trim()}.vault.azure.net/
              </div>
            )}
            {srcEnv && tgtEnv && (
              <div style={{ fontSize: 12, color: COLORS.muted, fontFamily: "monospace", background: COLORS.surface, borderRadius: 8, padding: "10px 14px", border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ color: COLORS.dim }}>{vault}</span>
                <span style={{ color: COLORS.dim }}>/</span>
                <span>{seedPrefix && <span style={{ color: COLORS.accent }}>{seedPrefix}-</span>}<span style={{ color: COLORS.orange }}>{srcEnv}</span>-*</span>
                <span style={{ color: COLORS.dim }}>→</span>
                <span style={{ color: COLORS.dim }}>{targetVault.trim() || vault}</span>
                <span style={{ color: COLORS.dim }}>/</span>
                <span>{seedPrefix && <span style={{ color: COLORS.accent }}>{seedPrefix}-</span>}<span style={{ color: COLORS.success }}>{tgtEnv}</span>-*</span>
              </div>
            )}
            {seedStep === "idle" && (
              <>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Btn variant="primary" onClick={previewSeed} disabled={seedLoading || !vault.trim() || !srcEnv.trim() || !tgtEnv.trim()}>
                    {seedLoading ? <><Spinner size={14} color="#fff" /> Fetching…</> : "Preview Secrets →"}
                  </Btn>
                </div>
                {seedLoading && (
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 12, padding: "40px 0", color: COLORS.muted,
                  }}>
                    <Spinner size={32} color={COLORS.accent} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>Fetching secrets matching {seedPrefix}-{srcEnv}-* …</span>
                  </div>
                )}
              </>
            )}
            {seedStep === "cloning" && (
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 12, padding: "40px 0", color: COLORS.muted,
              }}>
                <Spinner size={32} color={COLORS.orange} />
                <span style={{ fontSize: 13, fontWeight: 500 }}>Cloning {seedPreview.length} secrets to {tgtEnv} …</span>
              </div>
            )}
            {seedStep === "preview" && seedPreview.length > 0 && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{seedPreview.length} secrets to clone</span>
                  <Badge color="accent">{srcEnv} → {tgtEnv}</Badge>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 400, overflowY: "auto" }}>
                  {seedPreview.map((item, i) => (
                    <div key={i} style={{ background: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: "10px 12px", fontSize: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ color: COLORS.muted, textDecoration: "line-through", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.source_name}</span>
                      </div>
                      <div style={{ color: COLORS.success, fontWeight: 600, marginBottom: 6 }}>→ {item.target_name}</div>
                      <input
                        value={item.value}
                        onChange={e => updateSeedValue(i, e.target.value)}
                        style={{
                          background: COLORS.bg, border: `1px solid ${COLORS.border}`,
                          borderRadius: 6, color: COLORS.text, fontSize: 12,
                          fontFamily: "monospace", padding: "6px 10px", outline: "none",
                          width: "100%", boxSizing: "border-box",
                        }}
                        onFocus={e => e.target.style.borderColor = COLORS.accent}
                        onBlur={e  => e.target.style.borderColor = COLORS.border}
                      />
                    </div>
                  ))}
                </div>

                {/* Overwrite toggle */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  background: COLORS.surface, borderRadius: 8, padding: "10px 14px",
                  border: `1px solid ${COLORS.border}`,
                }}>
                  <input type="checkbox" id="seedOverwrite" checked={seedOverwrite}
                    onChange={e => setSeedOverwrite(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <label htmlFor="seedOverwrite" style={{ fontSize: 13, color: COLORS.text, cursor: "pointer" }}>
                    Overwrite existing target secrets
                  </label>
                  <span style={{ fontSize: 11, color: COLORS.muted }}>
                    {seedOverwrite ? "— existing target secrets will be overwritten" : "— existing target secrets will be skipped"}
                  </span>
                </div>

                <div style={{ background: "#2d1f00", border: "1px solid #f59e0b44", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#f59e0b" }}>
                  ⚠ This will create <strong>{seedPreview.length} new secrets</strong> in vault <strong>{targetVault.trim() || vault}</strong>.{targetVault.trim() && targetVault.trim() !== vault.trim() && <> Source vault: <strong>{vault}</strong>.</>} Source secrets will NOT be modified.
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Btn variant="ghost" onClick={resetSeed}>Cancel</Btn>
                  <Btn variant="orange" onClick={confirmSeed} disabled={seedLoading}>
                    {seedLoading ? <><Spinner size={14} color="#fff" /> Cloning…</> : `Clone ${seedPreview.length} Secrets`}
                  </Btn>
                </div>
              </>
            )}
            {seedStep === "done" && seedResults && (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {seedResults.created > 0     && <Badge color="success">{seedResults.created} created</Badge>}
                  {seedResults.overwritten > 0  && <Badge color="accent">{seedResults.overwritten} overwritten</Badge>}
                  {seedResults.skipped > 0      && <Badge color="warn">{seedResults.skipped} skipped</Badge>}
                  {seedResults.failed > 0       && <Badge color="danger">{seedResults.failed} failed</Badge>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 400, overflowY: "auto" }}>
                  {seedResults.results.map((r, i) => {
                    const color = r.status === "created" ? COLORS.success
                                : r.status === "overwritten" ? COLORS.accent
                                : r.status === "skipped" ? COLORS.warn
                                : COLORS.danger;
                    const icon  = r.status === "created" ? "✓" : r.status === "overwritten" ? "↺" : r.status === "skipped" ? "⊘" : "✗";
                    return (
                      <div key={i} style={{
                        background: COLORS.surface, borderRadius: 8,
                        border: `1px solid ${color}33`,
                        padding: "8px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10,
                      }}>
                        <span style={{ fontSize: 12, fontFamily: "monospace", color: COLORS.text, flex: 1, minWidth: 0, wordBreak: "break-all" }}>
                          {r.name}
                        </span>
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color }}>{icon} {r.status}</span>
                          {r.detail && <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>{r.detail}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        )}

        {/* DELETE BY PREFIX */}
        {tab === "delete" && (
          <Card title="Delete Secrets by Prefix" icon="🗑️" action={
            delStep !== "idle" && <Btn small variant="ghost" onClick={resetDelete}>↺ Reset</Btn>
          }>
            <div style={{ background: COLORS.dangerDim, border: `1px solid ${COLORS.danger}33`, borderRadius: 10, padding: "12px 16px", fontSize: 12, color: COLORS.muted, lineHeight: 1.7 }}>
              <span style={{ fontWeight: 600, color: COLORS.danger }}>⚠ Danger Zone: </span>
              Enter prefix (e.g. <code>RF</code>) and env code (e.g. <code>qa01slb</code>).
              All secrets matching <code>RF-qa01slb-*</code> will be shown for review before deletion.
              <strong> This action cannot be undone.</strong>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Input label="Secret Prefix" value={delPrefix} onChange={setDelPrefix} placeholder="e.g. RF" mono />
              <Input label="Env Code" value={delEnvCode} onChange={setDelEnvCode} placeholder="e.g. qa01slb" mono />
            </div>
            {delEnvCode && (
              <div style={{ fontSize: 12, color: COLORS.muted, fontFamily: "monospace", background: COLORS.surface, borderRadius: 8, padding: "10px 14px", border: `1px solid ${COLORS.border}` }}>
                Pattern: <span style={{ color: COLORS.accent }}>{delPrefix}-</span><span style={{ color: COLORS.danger }}>{delEnvCode}</span>-*
              </div>
            )}
            {delStep === "idle" && (
              <>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Btn variant="danger" onClick={previewDelete} disabled={delLoading || !vault.trim() || !delEnvCode.trim()}>
                    {delLoading ? <><Spinner size={14} color={COLORS.danger} /> Fetching…</> : "Preview Secrets →"}
                  </Btn>
                </div>
                {delLoading && (
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 12, padding: "40px 0", color: COLORS.muted,
                  }}>
                    <Spinner size={32} color={COLORS.danger} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>Fetching secrets matching {delPrefix}-{delEnvCode}-* …</span>
                  </div>
                )}
              </>
            )}
            {delStep === "deleting" && (
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 12, padding: "40px 0", color: COLORS.muted,
              }}>
                <Spinner size={32} color={COLORS.danger} />
                <span style={{ fontSize: 13, fontWeight: 500 }}>Deleting {delPreview.filter(s => s.selected).length} secrets …</span>
              </div>
            )}
            {delStep === "preview" && delPreview.length > 0 && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                    {delPreview.filter(s => s.selected).length} / {delPreview.length} secrets selected
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn small variant="ghost" onClick={() => toggleAllDelSecrets(true)}>Select All</Btn>
                    <Btn small variant="ghost" onClick={() => toggleAllDelSecrets(false)}>Deselect All</Btn>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 400, overflowY: "auto" }}>
                  {delPreview.map((item, i) => (
                    <div key={i} style={{
                      background: COLORS.surface, borderRadius: 8,
                      border: `1px solid ${item.selected ? COLORS.danger + '55' : COLORS.border}`,
                      padding: "10px 12px", display: "flex", alignItems: "center", gap: 10,
                      opacity: item.selected ? 1 : 0.5,
                    }}>
                      <input type="checkbox" checked={item.selected} onChange={() => toggleDelSecret(i)}
                        style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontFamily: "monospace", color: COLORS.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.name}
                      </span>
                      {item.selected && <span style={{ fontSize: 11, color: COLORS.danger, fontWeight: 600, flexShrink: 0 }}>will be deleted</span>}
                    </div>
                  ))}
                </div>
                <div style={{ background: "#2d0e0e", border: "1px solid #ef444444", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: COLORS.danger }}>
                  ⚠ This will permanently delete <strong>{delPreview.filter(s => s.selected).length} secret(s)</strong> from vault <strong>{vault}</strong>. This cannot be undone.
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Btn variant="ghost" onClick={resetDelete}>Cancel</Btn>
                  <Btn variant="danger" onClick={confirmDelete} disabled={delLoading || delPreview.filter(s => s.selected).length === 0}>
                    {delLoading ? <><Spinner size={14} color={COLORS.danger} /> Deleting…</> : `Delete ${delPreview.filter(s => s.selected).length} Secrets`}
                  </Btn>
                </div>
              </>
            )}
            {delStep === "done" && delResults && (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {delResults.deleted > 0 && <Badge color="success">{delResults.deleted} deleted</Badge>}
                  {delResults.failed > 0  && <Badge color="danger">{delResults.failed} failed</Badge>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 400, overflowY: "auto" }}>
                  {delResults.results.map((r, i) => {
                    const color = r.status === "deleted" ? COLORS.success : COLORS.danger;
                    const icon  = r.status === "deleted" ? "✓" : "✗";
                    return (
                      <div key={i} style={{
                        background: COLORS.surface, borderRadius: 8,
                        border: `1px solid ${color}33`,
                        padding: "8px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10,
                      }}>
                        <span style={{ fontSize: 12, fontFamily: "monospace", color: COLORS.text, flex: 1, minWidth: 0, wordBreak: "break-all" }}>
                          {r.name}
                        </span>
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color }}>{icon} {r.status}</span>
                          {r.detail && <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>{r.detail}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        )}
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} />}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const { accounts } = useMsal();
  const stored = sessionStorage.getItem("kv_user");
  const ssoAccount = accounts[0];
  const ssoUser = ssoAccount ? {
    username: ssoAccount.name || ssoAccount.username,
    name:     ssoAccount.name,
    email:    ssoAccount.username,
    authType: "sso",
  } : null;
  const [pwUser, setPwUser] = useState(stored ? JSON.parse(stored) : null);
  const activeUser = ssoUser || pwUser;

  const handlePasswordLogin = (userData) => setPwUser(userData);
  const handleLogout = () => { sessionStorage.removeItem("kv_user"); setPwUser(null); };

  if (!activeUser) return <LoginScreen onPasswordLogin={handlePasswordLogin} />;
  return <MainApp user={activeUser} onLogout={handleLogout} />;
}